import { useCallback } from "react";
import { toastManager, type DragResult } from "@salestrak/ui";
import type { IssueRow } from "#app/lib/data";
import { useKanbanMutations } from "#app/hooks/kanban/use-kanban-mutations";
import { useBoardReassign } from "#app/hooks/kanban/use-board-reassign";
import { useBoardSelection } from "#app/hooks/kanban/use-board-selection";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  useAnalytics,
  AnalyticsContext,
  AnalyticsView,
  KanbanEvent,
} from "#app/lib/analytics";
import type { BoardGroupDimension } from "#app/components/issues/board-columns";
import type { BoardColumn } from "./use-issues-board-columns";

/**
 * The drop grammar per dimension: `stage` moves and reorders (a group drag
 * lands the whole selection); `assignee` and `teams` reassign on a
 * cross-column drop. The stage move is also what the cards' stage chips use,
 * so it is returned beside the drop handler.
 */
export function useIssuesBoardDrag({
  groupBy,
  sortActive,
  selectedRowData,
}: {
  groupBy: BoardGroupDimension;
  sortActive: boolean;
  selectedRowData: ReturnType<typeof useBoardSelection>["selectedRowData"];
}) {
  const { track } = useAnalytics();
  const { moveIssueToStage, reorderIssue, dropIssues } = useKanbanMutations();
  const { reassignIssues } = useBoardReassign();

  const handleDragEnd = useCallback(
    async (result: DragResult<IssueRow, BoardColumn>) => {
      const {
        item,
        fromColumn,
        toColumn,
        position,
        upperNeighborId,
        lowerNeighborId,
        upperSortValue,
        lowerSortValue,
        atUnloadedTail,
        groupIds,
      } = result;

      // The membership groupings: a drop on another column reassigns the
      // card's assignee/team; within-column order stays server-sorted, so a
      // same-column drop has nothing to land.
      if (groupBy === "assignee" || groupBy === "teams") {
        const fromKey = fromColumn.key;
        const toKey = toColumn.key;
        if (fromKey === toKey) {
          toastManager.add({
            type: "info",
            title: "Reordering is off in this grouping",
            description:
              "Drop a card on another column to reassign it. Group by Stage to arrange cards by hand.",
          });
          return;
        }
        // A group drag reassigns the whole selection; rows resolve from the
        // selection map, the active card straight from the result.
        const rows =
          groupIds && groupIds.length > 1
            ? groupIds
                .map((id) =>
                  String(id) === item.issueId
                    ? item
                    : selectedRowData.get(String(id)),
                )
                .filter((r): r is IssueRow => r != null)
            : [item];
        try {
          const updated = await reassignIssues({
            rows,
            dimension: groupBy,
            fromKey,
            toKey,
          });
          // Selection outlives the drop, so refresh each kept row with its
          // server-confirmed assignees/teams — the next group drag derives
          // every row's membership from this map, and a stale list would
          // resurrect an assignment the card already lost.
          for (const patch of updated) {
            const kept = selectedRowData.get(patch.issueId);
            if (kept) selectedRowData.set(patch.issueId, { ...kept, ...patch });
          }
          // A single card lands where it was dropped; only a group drag
          // says how many moved, since the rest may sit off-screen.
          if (rows.length > 1) {
            const noun = `${rows.length.toLocaleString()} Projects`;
            toastManager.add({
              type: "success",
              title: toColumn.isUnassigned
                ? groupBy === "assignee"
                  ? `${noun} unassigned`
                  : `${noun} removed from all teams`
                : `${noun} assigned to ${toColumn.label}`,
            });
          }
          track({
            context: AnalyticsContext.Kanban,
            event: KanbanEvent.DragReassign,
            data: { view: AnalyticsView.Board, dimension: groupBy, count: rows.length },
          });
        } catch (error) {
          toastMutationError(error, "Failed to reassign Project");
        }
        return;
      }

      // Everything below is the stage grammar; the library skips this callback
      // for drop-disabled groupings, but guard defensively.
      if (groupBy !== "stage") return;
      const fromStageId = fromColumn.key;
      const toStageId = toColumn.key;
      const rejectSortedReorder = () => {
        toastManager.add({
          type: "info",
          title: "Reordering is off while sorted",
          description:
            "Cards follow the Sort by order in each stage. Clear Sort by to arrange them by hand. Moving a Project to another stage still works.",
        });
      };
      const upperNeighborKey = upperNeighborId != null ? String(upperNeighborId) : undefined;
      const lowerNeighborKey = lowerNeighborId != null ? String(lowerNeighborId) : undefined;

      // A group drag lands the whole selection contiguously at the drop slot.
      // Rows resolve from the selection map (the active card straight from the
      // result), ordered by current sortOrder so the block keeps its relative
      // order. Selection is kept after the drop — the wash follows the cards.
      if (groupIds && groupIds.length > 1) {
        const rows = groupIds
          .map((id) =>
            String(id) === item.issueId
              ? item
              : selectedRowData.get(String(id)),
          )
          .filter((r): r is IssueRow => r != null)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        const anyStageChange = rows.some((r) => r.stageId !== toStageId);
        if (sortActive && !anyStageChange) {
          rejectSortedReorder();
          return;
        }
        try {
          const moved = await dropIssues({
            rows,
            toStageId,
            upperNeighborKey,
            upperSortValue,
            lowerSortValue,
            atUnloadedTail,
          });
          // Selection outlives the drop, so refresh each kept row with its
          // server-confirmed stage/sort — the next group drag derives every
          // row's fromStageId from this map, and a stale stageId would remove
          // the card from a column it already left.
          for (const row of moved) {
            if (selectedRowData.has(row.issueId)) {
              selectedRowData.set(row.issueId, row);
            }
          }
          track({
            context: AnalyticsContext.Kanban,
            event: anyStageChange
              ? KanbanEvent.DragMoveStage
              : KanbanEvent.DragReorder,
            data: { view: AnalyticsView.Board, count: rows.length },
          });
        } catch (error) {
          toastMutationError(error, "Failed to move Projects");
        }
        return;
      }

      if (sortActive && fromStageId === toStageId) {
        rejectSortedReorder();
        return;
      }
      try {
        if (fromStageId !== toStageId) {
          await moveIssueToStage({
            issue: item,
            fromStageId,
            toStageId,
            newSortOrder: position,
            upperNeighborKey,
          });
          track({
            context: AnalyticsContext.Kanban,
            event: KanbanEvent.DragMoveStage,
            data: { view: AnalyticsView.Board, count: 1 },
          });
        } else {
          await reorderIssue({
            issue: item,
            stageId: toStageId,
            upperNeighborKey,
            lowerNeighborKey,
            newSortOrder: position,
          });
          track({
            context: AnalyticsContext.Kanban,
            event: KanbanEvent.DragReorder,
            data: { view: AnalyticsView.Board, count: 1 },
          });
        }
      } catch (error) {
        toastMutationError(error, "Failed to move Project");
      }
    },
    [
      groupBy,
      sortActive,
      moveIssueToStage,
      reorderIssue,
      dropIssues,
      reassignIssues,
      selectedRowData,
      track,
    ],
  );

  return { handleDragEnd, moveIssueToStage };
}
